/** @jsx jsx */
import { jsx } from '@emotion/react';
import { Spacer, colors } from '@smartsheet/lodestar-core';
import {
  FeedbackIcon,
  HelpIcon,
  PlayIcon,
  SettingsIcon,
  SyncLeftIcon,
  ToolbarSaveIcon,
  ToolbarUndoIcon,
} from '@smartsheet/lodestar-icons';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { startWorkflowRun } from '../../../services/api/WorkflowService/WorkflowService';
import {
  isWorkflowSavingSelector,
  hasChangedSelector,
  saveWorkflowData,
  switchToLegacyView,
} from '../../../services/ducks/workflowDuck/workflowDuck';
import { IconButton } from '../../common/IconButton';
import { Bar, Left, Right, Title, TitleGrey } from './styled';

interface DesignerHeaderBarProps {
  workspaceName: string;
  workflowName: string;
  backButtonHandler: () => any;
  navigateToHelp: () => any;
  navigateToFeedback: () => any;
}

export const DesignerHeaderBar = ({
  workspaceName,
  workflowName,
  backButtonHandler,
  navigateToHelp,
  navigateToFeedback,
}: DesignerHeaderBarProps) => {
  const { t: translate } = useTranslation();

  const isSaving = useSelector(isWorkflowSavingSelector);
  const hasChanged = useSelector(hasChangedSelector);

  const dispatch = useDispatch();

  const saveWorkflowAction = () => {
    dispatch(saveWorkflowData());
  };

  const switchToLegacyViewAction = () => {
    dispatch(switchToLegacyView());
  };

  return (
    <Bar>
      <Left>
        <Spacer orientation="row" space="small">
          <IconButton
            appearance="borderless"
            onClick={() => {
              backButtonHandler();
            }}
            iconBefore={<SyncLeftIcon color={colors.neutralDark40} />}
            name="Back"
            aria-label={translate('common:back')}
          />
          <TitleGrey>{workspaceName}</TitleGrey>
          <TitleGrey>/</TitleGrey>
          <Title>{workflowName}</Title>
        </Spacer>
      </Left>
      <Right>
        <Spacer orientation="row" space="xLarge">
          <IconButton
            appearance="secondary"
            onClick={switchToLegacyViewAction}
            name="SwitchToLegacyView"
            aria-label={translate('designer:topRail.switchToLegacyView')}
          >
            {translate('designer:topRail.switchToLegacyView')}
          </IconButton>
          <IconButton
            appearance="secondary"
            iconBefore={<ToolbarUndoIcon />}
            name={translate('common:undo')}
            aria-label={translate('common:undo')}
            isDisabled
          />
          <Spacer orientation="row" space="small">
            <IconButton
              appearance="secondary"
              iconBefore={<SettingsIcon />}
              name={translate('designer:topRail.settings')}
              aria-label={translate('designer:topRail.settings')}
              isDisabled
            />
            <IconButton
              appearance="secondary"
              iconBefore={<FeedbackIcon />}
              name={translate('common:feedback')}
              onClick={navigateToFeedback}
              aria-label={translate('common:feedback')}
              title="Send us feedback"
            />
            <IconButton
              appearance="secondary"
              iconBefore={<HelpIcon />}
              name={translate('common:help')}
              aria-label={translate('common:help')}
              onClick={navigateToHelp}
            />
            <IconButton
              appearance="secondary"
              iconBefore={<PlayIcon />}
              name={translate('designer:runLog.runWorkflow')}
              onClick={() => {
                startWorkflowRun(workflowName);
              }}
              aria-label={translate('designer:runLog.runWorkflow')}
            />
            <IconButton
              iconBefore={<ToolbarSaveIcon />}
              name={translate('common:save')}
              aria-label={translate('common:save')}
              onClick={saveWorkflowAction}
              isDisabled={isSaving || !hasChanged}
              isLoading={isSaving}
            >
              {translate('common:save')}
            </IconButton>
          </Spacer>
        </Spacer>
      </Right>
    </Bar>
  );
};
